// Authentication service with conditional mock/real implementation
import config from '../config';
import * as mockAuth from './mockAuth';

// Import types
export interface AuthResult {
  success: boolean;
  message?: string;
  session?: any;
  userId?: string;
}

export interface RegisterData {
  email: string;
  password: string;
}

export interface LoginData {
  email: string;
  password: string;
}

// Real API implementation using backend endpoints
const realAuth = {
  register: async (data: RegisterData): Promise<AuthResult> => {
    try {
      const response = await fetch(`${config.apiGatewayUrl}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: data.email,
          password: data.password,
        }),
      });

      const result = await response.json();

      if (response.ok) {
        return {
          success: true,
          message: result.message || 'Registration successful! Your account is pending admin approval. You will be notified once approved.',
          userId: result.userId,
        };
      } else {
        return {
          success: false,
          message: result.error || 'Registration failed',
        };
      }
    } catch (error) {
      return {
        success: false,
        message: 'Network error. Please try again.',
      };
    }
  },

  login: async (data: LoginData): Promise<AuthResult> => {
    try {
      const response = await fetch(`${config.apiGatewayUrl}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: data.email,
          password: data.password,
        }),
      });

      const result = await response.json();

      if (response.ok) {
        // Store JWT tokens in localStorage
        localStorage.setItem('authToken', result.accessToken); // IdToken for API calls
        localStorage.setItem('cognitoAccessToken', result.cognitoAccessToken); // AccessToken for logout
        localStorage.setItem('refreshToken', result.refreshToken);
        localStorage.setItem('userEmail', data.email);
        localStorage.setItem('userId', result.userId);

        return {
          success: true,
          session: result,
          userId: result.userId,
        };
      } else {
        // Handle approval status messages
        if (response.status === 403) {
          return {
            success: false,
            message: result.error || 'Account not approved',
          };
        }
        return {
          success: false,
          message: result.error || 'Login failed',
        };
      }
    } catch (error) {
      return {
        success: false,
        message: 'Network error. Please try again.',
      };
    }
  },

  logout: async (): Promise<void> => {
    try {
      const cognitoAccessToken = localStorage.getItem('cognitoAccessToken');
      if (cognitoAccessToken) {
        await fetch(`${config.apiGatewayUrl}/auth/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${cognitoAccessToken}`,
          },
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local storage regardless of API call result
      localStorage.removeItem('authToken');
      localStorage.removeItem('cognitoAccessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userEmail');
      localStorage.removeItem('userId');
    }
  },

  getCurrentUser: (): any => {
    const email = localStorage.getItem('userEmail');
    const userId = localStorage.getItem('userId');
    if (!email || !userId) return null;
    return { email, userId };
  },

  getAuthToken: (): string | null => {
    return localStorage.getItem('authToken');
  },

  isAuthenticated: (): boolean => {
    const token = localStorage.getItem('authToken');
    return token !== null;
  },

  getCurrentSession: async (): Promise<any> => {
    const token = localStorage.getItem('authToken');
    const email = localStorage.getItem('userEmail');
    const userId = localStorage.getItem('userId');
    
    if (!token || !email || !userId) {
      return null;
    }

    // Simple token validation - check if it exists
    // In production, you might want to validate token expiry
    return {
      accessToken: token,
      email,
      userId,
    };
  },

  verifyEmail: async (email: string, code: string): Promise<AuthResult> => {
    // Email verification not required for MVP
    return {
      success: true,
      message: 'Email verification not required for MVP',
    };
  },

  resendVerificationCode: async (email: string): Promise<AuthResult> => {
    // Email verification not required for MVP
    return {
      success: true,
      message: 'Email verification not required for MVP',
    };
  },

  forgotPassword: async (email: string): Promise<AuthResult> => {
    try {
      const response = await fetch(`${config.apiGatewayUrl}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const result = await response.json();

      if (response.ok) {
        return {
          success: true,
          message: result.message || 'Password reset code sent to your email',
        };
      } else {
        return {
          success: false,
          message: result.error || 'Failed to send reset code',
        };
      }
    } catch (error) {
      return {
        success: false,
        message: 'Network error. Please try again.',
      };
    }
  },

  resetPassword: async (
    email: string,
    code: string,
    newPassword: string
  ): Promise<AuthResult> => {
    try {
      const response = await fetch(`${config.apiGatewayUrl}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          code,
          newPassword,
        }),
      });

      const result = await response.json();

      if (response.ok) {
        return {
          success: true,
          message: result.message || 'Password reset successful',
        };
      } else {
        return {
          success: false,
          message: result.error || 'Failed to reset password',
        };
      }
    } catch (error) {
      return {
        success: false,
        message: 'Network error. Please try again.',
      };
    }
  },
};

// Select the appropriate auth service based on config
const authService = config.useMockBackend ? mockAuth : realAuth;

// Log which service is being used
if (config.useMockBackend) {
  console.log('[Auth Service] Using MOCK authentication service');
} else {
  console.log('[Auth Service] Using real API authentication service');
}

// Export all functions from the selected service
export const register = authService.register;
export const login = authService.login;
export const logout = authService.logout;
export const getCurrentUser = authService.getCurrentUser;
export const getAuthToken = authService.getAuthToken;
export const isAuthenticated = authService.isAuthenticated;
export const getCurrentSession = authService.getCurrentSession;
export const verifyEmail = authService.verifyEmail;
export const resendVerificationCode = authService.resendVerificationCode;
export const forgotPassword = authService.forgotPassword;
export const resetPassword = authService.resetPassword;
